import {find, isUndefined} from 'lodash';

import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {batch} from '../../../helpers/reduxHelpers';
import {ConversationModel, parseTrackers} from '../../../models/api/conversationModel';
import {ApiConversationDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {PrecogModel} from '../../../models/api/precogModel';
import {fetchApiAsync} from '../../../services/http/http';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {isConversationInCells} from '../../panels/cells/helpers/cellsPanelHelpers';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSelectionParameters,
  InboxesSelectionTypesEnum,
} from '../../panels/inboxes/parameters/inboxesParameters';
import {
  FetchConversationCachePoliciesEnum,
  navigateViewerAsyncAction,
} from '../../panels/viewer/viewerPanelActions';
import {
  getViewerPanelsConversationIds,
  makeGetViewerPanelsForConversations,
} from '../../panels/viewer/viewerPanelSelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getConversations, getOriginalConversations} from '../dataSelectors';
import {updateConversationPrecogs} from '../precogs/conversationPrecogsActions';
import {clearConversationContentAction, conversationDeletedAction} from './conversationsActions';
import {makeGetConversationById} from './conversationsSelectors';

export function pushConversationUpdatedAction(
  conversationId: number,
  dataGroup: DataGroupModel,
  requesterId?: number,
): ThunkAction {
  return (dispatch, getState) => {
    if (isUndefined(dataGroup.conversations)) {
      throw new Error('Expected a data group with a conversation array.');
    }

    const conversation = find(dataGroup.conversations, {id: conversationId});
    if (isUndefined(conversation)) {
      throw new ErrorWithData('Expected a data group with conversation id', {
        expectedConversationId: conversationId,
        conversations: dataGroup.conversations?.map(({id, updatedAt}) => ({id, updatedAt})),
      });
    }

    const state = getState();

    // If the existing conversation WITHOUT optimistic updates is more recent, nothing to do.
    // Note: this is an optimization only, our update may still be ignored once it goes through the task queue.
    const existingOriginalConversation = getOriginalConversations(state)[conversation.id];
    if (existingOriginalConversation && existingOriginalConversation.updatedAt > conversation.updatedAt) {
      return;
    }

    const viewerPanelsConversationIds = getViewerPanelsConversationIds(state);
    const getConversationById = makeGetConversationById(conversation.id);
    const existingConversation = getConversationById(state);

    // Maybe remove last message to fix P2/P3 update discrepancy when there is a new message.
    const fixedConversation = maybeRemoveLastMessageFromConversation(
      conversation,
      existingConversation,
      viewerPanelsConversationIds,
    );
    const newConversations = (dataGroup.conversations || []).map((c) =>
      c === conversation ? fixedConversation : c,
    );

    const newDataGroup: DataGroupModel = {
      ...dataGroup,
      conversations: newConversations,
    };

    dispatch(
      updateDataAction(newDataGroup, {
        isComingFromPusher: true,
        requesterId,
      }),
    );
  };
}

export function pushConversationPrecogsUpdatedAction(
  conversationId: number,
  conversationUpdatedAt: number | undefined,
  conversationPrecogs: ReadonlyArray<PrecogModel> | undefined,
  conversationSentPrecogs: ReadonlyArray<PrecogModel> | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    const existingConversation = getOriginalConversations(state)[conversationId];

    // Pusher events may arrive out-of-order, but as precogs are short-lived,
    // we can be more permissive and update P2 cells even if the conversation
    // is not in store (e.g. search results).
    if (
      conversationUpdatedAt && existingConversation
        ? existingConversation.updatedAt > conversationUpdatedAt
        : !isConversationInCells(state, conversationId)
    ) {
      return;
    }

    const previousPrecogs = state.originalData.conversationPrecogs[conversationId] ?? [];

    dispatch(
      updateConversationPrecogs(
        conversationId,
        conversationPrecogs ?? previousPrecogs, // Use previous precogs if the update doesn't contain precogs.
        conversationSentPrecogs,
      ),
    );
  };
}

// TODO: Type `conversationData` and require upstream to validate the pusher event data with `io-ts`.
// This doesn't update precogs, see pushConversationPrecogsUpdatedAction above.
export function pushPartialConversationUpdatedAction(
  conversationId: number,
  conversationData: any,
): ThunkAction {
  return (dispatch, getState) => {
    if (!conversationData.updated_at) {
      return;
    }

    // If the existing conversation is more recent, nothing to do.
    const state = getState();
    const existingConversation = getOriginalConversations(state)[conversationId];
    if (!existingConversation || existingConversation.updatedAt > conversationData.updated_at) {
      return;
    }

    // Since this is not a full conversation, keep existingConversation.updatedAt the same.
    const newConversation: ConversationModel = {
      ...existingConversation,
      contentUpdatedDates: conversationData.content_updated_dates,
      // TODO: Should these always exist?
      ...(conversationData.trackers && conversationData.seen_by
        ? {trackers: parseTrackers(conversationData.trackers, conversationData.seen_by)}
        : {}),
    };

    const requesterId = Number(conversationData && conversationData.requester_id) || undefined;

    dispatch(pushConversationUpdatedAction(conversationId, {conversations: [newConversation]}, requesterId));
  };
}

export function pushConversationsJoinedAsyncAction(
  winningConversationId: number,
  losingConversationIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the existing conversation.
    const conversations = getConversations(getState());
    const existingConversation = conversations[winningConversationId];

    if (!existingConversation) {
      return;
    }

    // Find the panel IDs and cells contexts in which the involved conversations appear.
    const involvedConversationIds = [winningConversationId, ...losingConversationIds];
    const getPanelsForConversations = makeGetViewerPanelsForConversations<void>(
      () => involvedConversationIds,
    );
    const panelsForConversations = getPanelsForConversations(getState());

    batch(() => {
      // Clear the content of the winning conversation.
      dispatch(clearConversationContentAction(winningConversationId));

      // Delete the losing conversations.
      losingConversationIds.forEach((id) => {
        dispatch(conversationDeletedAction(id));
      });
    });

    // If the affected conversation are in at least one panel, we need to refresh those.
    if (panelsForConversations.length > 0) {
      const refetchPromises = panelsForConversations.map(({panelId, parameters}) => {
        const selection: InboxesSelectionParameters = {
          type: InboxesSelectionTypesEnum.NORMAL,
          selectedIds: [winningConversationId],
        };
        const newParameters: InboxesParameters =
          parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES
            ? parameters
            : {...parameters, selection, activity: undefined};

        return dispatch(
          navigateViewerAsyncAction(panelId, newParameters, {
            cachePolicy: FetchConversationCachePoliciesEnum.NETWORK_ONLY,
          }),
        );
      });

      await Promise.all(refetchPromises);
      return;
    }

    const winningConversationUrl = existingConversation.url;
    const dataGroup = await fetchApiAsync(ApiConversationDataGroup, winningConversationUrl);
    // Otherwise, we can just update the conversation as-is
    dispatch(pushConversationUpdatedAction(winningConversationId, dataGroup));
  };
}

/*
 * Helpers.
 */

function maybeRemoveLastMessageFromConversation(
  newConversation: ConversationModel,
  existingConversation: ConversationModel | undefined,
  viewerPanelsConversationIds: ReadonlyArray<number>,
) {
  if (!existingConversation) {
    // If we don't have the existing conversation, just use the new one.
    return newConversation;
  }

  if (!viewerPanelsConversationIds.includes(newConversation.id)) {
    // If we aren't subscribed to the pusher room for conversation, use the new lastMessage since we won't get an update.
    return newConversation;
  }

  if (
    newConversation.lastMessage &&
    existingConversation.lastMessage &&
    newConversation.lastMessage.sentAt < existingConversation.lastMessage.sentAt
  ) {
    // If the new conversation has a last message that is older, the existing last message might be deleted, so let's keep the new one.
    return newConversation;
  }

  // If we're subscribed to this conversation's room, lastMessage will be updated via activity_created.
  return {
    ...newConversation,
    lastMessage: existingConversation.lastMessage,
  };
}
